import {
    Badge,
    Button,
    Group,
    Modal,
    Stack,
    Text,
} from '@mantine/core';
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { Product } from '../types';
import { SubscriptionStatusInfo } from '../hooks/useSubscriptionStatus';
import {
    cancelSubscriptionRecord,
    subscribeToProduct,
} from '../../subscriptions/services/SubscriptionsService';
import { useUserAccount } from '../../../../hooks/useUserAccount';
import { PlanAction } from './PlanComparisonTable';
import { AiOutlineArrowUp, AiOutlineArrowDown } from 'react-icons/ai';

const formatPrice = (amount: number) => `$${(amount / 100).toFixed(2)}`;

interface PlanChangeModalProps {
    opened: boolean;
    onClose: () => void;
    targetProduct: Product | null;
    currentProduct: Product | null;
    action: PlanAction;
    statusByProductId: Record<string, SubscriptionStatusInfo>;
}

export const PlanChangeModal = ({
    opened,
    onClose,
    targetProduct,
    currentProduct,
    action,
    statusByProductId,
}: PlanChangeModalProps) => {
    const [loading, setLoading] = useState(false);
    const queryClient = useQueryClient();
    const { userAccount: account } = useUserAccount();

    if (!targetProduct) return null;

    const targetPrice = targetProduct.prices?.items?.[0];
    const currentInfo = currentProduct ? statusByProductId[currentProduct.id] : undefined;
    const isUpgrade = action === 'upgrade';

    const handleConfirm = async () => {
        if (!account?.customerId || !targetPrice) return;

        setLoading(true);
        try {
            // Cancel current subscription if upgrading/downgrading
            if (currentInfo?.subscriptionId) {
                await cancelSubscriptionRecord(currentInfo.subscriptionId);
            }

            // Create new subscription
            await subscribeToProduct(account.customerId, targetPrice.id, 1, queryClient);

            // Invalidate caches
            await queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
            await queryClient.invalidateQueries({ queryKey: ['products'] });

            notifications.show({
                title: isUpgrade ? 'Plan Upgraded' : action === 'downgrade' ? 'Plan Downgraded' : 'Subscribed',
                message: `You are now on the ${targetProduct.name} plan.`,
                color: 'green',
            });

            onClose();
        } catch (e) {
            notifications.show({
                title: 'Error',
                message: 'Failed to change plan. Please try again.',
                color: 'red',
            });
        } finally {
            setLoading(false);
        }
    };

    const priceDiff = (targetPrice?.amount ?? 0) - (currentInfo?.amount ?? 0);

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={
                <Group gap="xs">
                    {isUpgrade ? <AiOutlineArrowUp color="var(--mantine-color-blue-6)" /> : <AiOutlineArrowDown color="var(--mantine-color-orange-6)" />}
                    <Text fw={700}>{isUpgrade ? 'Upgrade' : 'Downgrade'} Plan</Text>
                </Group>
            }
            centered
        >
            <Stack gap="md">
                {currentProduct && (
                    <Group justify="space-between">
                        <Text size="sm" c="dimmed">Current plan:</Text>
                        <Group gap="xs">
                            <Text fw={500}>{currentProduct.name}</Text>
                            {currentInfo?.amount != null && (
                                <Badge variant="light">{formatPrice(currentInfo.amount)}/{currentInfo.billingPeriod}</Badge>
                            )}
                        </Group>
                    </Group>
                )}

                <Group justify="space-between">
                    <Text size="sm" c="dimmed">New plan:</Text>
                    <Group gap="xs">
                        <Text fw={500}>{targetProduct.name}</Text>
                        {targetPrice && (
                            <Badge variant="light" color={isUpgrade ? 'blue' : 'orange'}>
                                {formatPrice(targetPrice.amount)}/{targetPrice.billingPeriod}
                            </Badge>
                        )}
                    </Group>
                </Group>

                {priceDiff !== 0 && (
                    <Text ta="center" size="sm" fw={500} c={isUpgrade ? 'blue' : 'orange'}>
                        {priceDiff > 0 ? '+' : ''}{formatPrice(priceDiff)}/{targetPrice?.billingPeriod}
                    </Text>
                )}

                <Group justify="flex-end" mt="md">
                    <Button variant="subtle" onClick={onClose}>Cancel</Button>
                    <Button
                        color={isUpgrade ? 'blue' : 'orange'}
                        onClick={handleConfirm}
                        loading={loading}
                    >
                        Confirm {isUpgrade ? 'Upgrade' : 'Downgrade'}
                    </Button>
                </Group>
            </Stack>
        </Modal>
    );
};
